/**
 * `/recuperer-mes-donnees` — récupération des données par le patron quand
 * l'abonnement ne court plus (décision 12 du 19 octobre 2026 ; Data Act,
 * RGPD 28.3.g). Une archive à télécharger, jamais de consultation : l'écran
 * ne montre aucune donnée de l'entreprise. Atteint seulement par le lien
 * discret de l'écran de suspension ; l'API revérifie l'identité. Après la fin
 * de l'abonnement, la page rappelle le dernier jour de la récupération (30 jours).
 * Une archive hors limites n'est jamais une impasse : la page dit que l'équipe
 * MyEquip a été prévenue d'elle-même et prépare la copie.
 */
import type { ReactNode } from 'react';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { DetailPage } from '../../components/templates/templates.js';
import { Banner, QueryBoundary } from '../../components/ui/Feedback.js';
import { ExternalShell } from '../../components/shell/ExternalShell.js';
import type { ButtonAction } from '../../components/ui/action.js';
import { canPrepareAnother, recoveryStep, useDataExport, type DataExportView } from './subscription-hooks.js';

const size = new Intl.NumberFormat('fr-FR', { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 });
const day = (iso: string): string => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
const moment = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

interface Gesture { run: () => void; pending: boolean }

/** Où en est la copie demandée d'elle-même à l'équipe MyEquip (archive hors limites). */
function copyMessage(request: NonNullable<DataExportView['copyRequest']>): string {
  return request.status === 'traitee'
    ? t('abonnement.recuperation.copie.traitee', { date: day(request.processedAt ?? request.createdAt) })
    : t('abonnement.recuperation.copie.demandee');
}

/** L'action de l'état courant : une seule (patienter, télécharger, préparer). */
function primaryAction(data: DataExportView | undefined, prepare: Gesture, download: Gesture): ButtonAction | undefined {
  const preparing = prepare.pending;
  switch (recoveryStep(data)) {
    case 'attendre':
      return { label: t('abonnement.recuperation.preparer'), onClick: () => undefined, pending: true };
    case 'telecharger':
      return { label: t('abonnement.recuperation.telecharger'), onClick: download.run, pending: download.pending };
    case 'preparer':
      return { label: t('abonnement.recuperation.preparer'), onClick: prepare.run, pending: preparing };
    case 'reessayer':
      return { label: t('abonnement.recuperation.reessayer'), onClick: prepare.run, pending: preparing };
    default:
      return undefined;
  }
}

export function DataRecoveryPage(): ReactNode {
  const { logout } = useAuth();
  const { owner, view, prepare, download } = useDataExport();
  const data = view.data;
  const running = data?.latest?.status === 'en_file' || data?.latest?.status === 'en_cours';
  const preparing = { run: () => prepare.mutate(), pending: prepare.isPending };
  const primary = owner
    ? primaryAction(data, preparing, { run: () => download.mutate(), pending: download.isPending })
    : undefined;
  // Archive encore valide, nouvelle préparation permise : geste secondaire.
  const secondary: ButtonAction | undefined = owner && canPrepareAnother(data)
    ? { label: t('abonnement.recuperation.reessayer'), onClick: preparing.run, pending: preparing.pending }
    : undefined;
  const status = running || !data ? undefined
    : data.nextAllowedAt ? t('abonnement.recuperation.prochaine', { date: moment(data.nextAllowedAt) })
      : data.download ? undefined : t('abonnement.recuperation.verification');

  return (
    <ExternalShell onLogout={() => void logout()}>
      <DetailPage
        header={{
          kicker: 'Abonnement MyEquip',
          title: t('abonnement.recuperation.titre'),
          description: t('abonnement.recuperation.description'),
          backTo: '/',
          backLabel: t('abonnement.recuperation.retour'),
        }}
        {...(primary ? { actionBar: { primary, ...(secondary ? { secondary } : {}), ...(status ? { status } : {}) } } : {})}
      >
        {owner ? (
          <QueryBoundary query={view}>
            {(current) => (
              <>
                {current.availableUntil ? (
                  <Banner tone="warn">
                    {t('abonnement.recuperation.disponibleJusquAu', { date: day(current.availableUntil) })}
                  </Banner>
                ) : null}
                {running ? <Banner tone="info">{t('abonnement.recuperation.enCours')}</Banner> : null}
                {!running && current.latest?.status === 'echec' ? (
                  current.copyRequest ? (
                    // Hors limites : la copie est déjà demandée à l'équipe MyEquip, rien à faire.
                    <Banner tone="warn" title={t('abonnement.recuperation.copie.titre')}>
                      {current.latest.error} {copyMessage(current.copyRequest)}
                    </Banner>
                  ) : (
                    <Banner tone="danger" title={t('abonnement.recuperation.echec')}>{current.latest.error}</Banner>
                  )
                ) : null}
                {current.download ? (
                  <Banner tone="info">
                    {t('abonnement.recuperation.prete', {
                      taille: size.format(current.download.sizeBytes / 1024 ** 2),
                      date: day(current.download.expiresAt),
                    })}
                  </Banner>
                ) : null}
                {!primary && status ? <Banner tone="info">{status}</Banner> : null}
                {prepare.error ? <Banner tone="danger">{(prepare.error as Error).message}</Banner> : null}
                {download.error ? <Banner tone="danger">{(download.error as Error).message}</Banner> : null}
              </>
            )}
          </QueryBoundary>
        ) : (
          <Banner tone="info">{t('abonnement.recuperation.reserve')}</Banner>
        )}
      </DetailPage>
    </ExternalShell>
  );
}
